import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const CreateChallengeModal = ({ onClose, onCreated, challenge }) => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clubs, setClubs] = useState([]);

  const isEditMode = !!challenge;

  // Map scope back to visibility for display
  const getVisibilityFromScope = (scope) => {
    if (scope === 'private') return 'private';
    if (scope === 'invite') return 'invite';
    if (scope === 'club') return 'club';
    return 'public';
  };

  const [formData, setFormData] = useState({
    title: challenge?.title || '',
    description: challenge?.description || '',
    challenge_type: challenge?.challenge_type || 'distance',
    target_value: challenge?.target_value?.toString() || '',
    target_unit: challenge?.target_unit || 'km',
    pace_target: challenge?.pace_target || '',
    start_date: challenge?.start_date || '',
    end_date: challenge?.end_date || '',
    visibility: challenge ? getVisibilityFromScope(challenge.scope) : 'public',
    club_id: challenge?.club_id?.toString() || ''
  });

  // Load user's clubs for the club visibility option
  useEffect(() => {
    const loadClubs = async () => {
      try {
        const res = await authFetch('/api/clubs/my');
        if (res.ok) {
          const data = await res.json();
          setClubs(data.clubs || []);
        }
      } catch (err) {
        console.error('Failed to load clubs:', err);
      }
    };
    loadClubs();
  }, [authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Auto-update target_unit based on challenge_type
      if (name === 'challenge_type') {
        if (value === 'distance') updated.target_unit = 'km';
        else if (value === 'streak') updated.target_unit = 'days';
        else if (value === 'pace') updated.target_unit = 'min/km';
      }

      // Reset club_id if visibility is not club
      if (name === 'visibility' && value !== 'club') {
        updated.club_id = '';
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Map visibility to scope
      let scope = 'public';
      if (formData.visibility === 'private') scope = 'private';
      else if (formData.visibility === 'invite') scope = 'invite';
      else if (formData.visibility === 'club') scope = 'club';

      const payload = {
        title: formData.title,
        description: formData.description,
        challenge_type: formData.challenge_type,
        target_value: parseFloat(formData.target_value),
        target_unit: formData.target_unit,
        pace_target: formData.challenge_type === 'pace' ? formData.pace_target : null,
        start_date: formData.start_date,
        end_date: formData.end_date,
        scope: scope,
        club_id: formData.visibility === 'club' ? parseInt(formData.club_id) : null
      };

      const url = isEditMode ? `/api/challenges/${challenge.id}` : '/api/challenges';
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        onCreated();
      } else {
        const data = await res.json();
        setError(data.detail || `Failed to ${isEditMode ? 'update' : 'create'} challenge`);
      }
    } catch (err) {
      setError(`Failed to ${isEditMode ? 'update' : 'create'} challenge`);
    } finally {
      setLoading(false);
    }
  };

  const getTargetPlaceholder = () => {
    switch (formData.challenge_type) {
      case 'distance': return '100';
      case 'streak': return '30';
      case 'pace': return '5:30';
      default: return '';
    }
  };

  const getTargetLabel = () => {
    switch (formData.challenge_type) {
      case 'distance': return 'Distance Goal';
      case 'streak': return 'Streak Days';
      case 'pace': return 'Target Pace';
      default: return 'Target';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-challenge-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Challenge/Goal' : 'Create Challenge/Goal'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., February 100km Challenge"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your challenge..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Challenge Type</label>
            <div className="challenge-type-options">
              <label className={`type-option ${formData.challenge_type === 'distance' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="challenge_type"
                  value="distance"
                  checked={formData.challenge_type === 'distance'}
                  onChange={handleChange}
                />
                <span className="type-icon">📏</span>
                <span className="type-label">Distance</span>
                <span className="type-desc">Total km/miles</span>
              </label>
              <label className={`type-option ${formData.challenge_type === 'streak' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="challenge_type"
                  value="streak"
                  checked={formData.challenge_type === 'streak'}
                  onChange={handleChange}
                />
                <span className="type-icon">🔥</span>
                <span className="type-label">Streak</span>
                <span className="type-desc">Consecutive days</span>
              </label>
              <label className={`type-option ${formData.challenge_type === 'pace' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="challenge_type"
                  value="pace"
                  checked={formData.challenge_type === 'pace'}
                  onChange={handleChange}
                />
                <span className="type-icon">⚡</span>
                <span className="type-label">Pace</span>
                <span className="type-desc">Hit target pace</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>{getTargetLabel()}</label>
            <div className="input-with-unit">
              <input
                type={formData.challenge_type === 'pace' ? 'text' : 'number'}
                name="target_value"
                value={formData.target_value}
                onChange={handleChange}
                placeholder={getTargetPlaceholder()}
                required
                min={formData.challenge_type !== 'pace' ? '1' : undefined}
              />
              <span className="unit">{formData.target_unit}</span>
            </div>
            {formData.challenge_type === 'pace' && (
              <small className="form-hint">Format: MM:SS (e.g., 5:30 for 5 min 30 sec per km)</small>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Who can join?</label>
            <div className="visibility-options">
              <label className={`visibility-option ${formData.visibility === 'public' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={formData.visibility === 'public'}
                  onChange={handleChange}
                />
                <span className="visibility-icon">🌍</span>
                <div className="visibility-text">
                  <span className="visibility-label">Public</span>
                  <span className="visibility-desc">Anyone can join</span>
                </div>
              </label>
              <label className={`visibility-option ${formData.visibility === 'private' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={formData.visibility === 'private'}
                  onChange={handleChange}
                />
                <span className="visibility-icon">🔒</span>
                <div className="visibility-text">
                  <span className="visibility-label">Private</span>
                  <span className="visibility-desc">Just for you</span>
                </div>
              </label>
              <label className={`visibility-option ${formData.visibility === 'invite' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="invite"
                  checked={formData.visibility === 'invite'}
                  onChange={handleChange}
                />
                <span className="visibility-icon">✉️</span>
                <div className="visibility-text">
                  <span className="visibility-label">Invite Only</span>
                  <span className="visibility-desc">Specific people</span>
                </div>
              </label>
              <label className={`visibility-option ${formData.visibility === 'club' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="club"
                  checked={formData.visibility === 'club'}
                  onChange={handleChange}
                />
                <span className="visibility-icon">👥</span>
                <div className="visibility-text">
                  <span className="visibility-label">Club/Team</span>
                  <span className="visibility-desc">Group members only</span>
                </div>
              </label>
            </div>
          </div>

          {formData.visibility === 'club' && (
            <div className="form-group">
              <label>Select Club/Team</label>
              <select
                name="club_id"
                value={formData.club_id}
                onChange={handleChange}
                required
              >
                <option value="">Choose a club...</option>
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>{club.name}</option>
                ))}
              </select>
              {clubs.length === 0 && (
                <small className="form-hint">You need to join or create a club first</small>
              )}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Challenge/Goal')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChallengeModal;
